/**
 * PocketFiler Language Configuration
 * 
 * Tier 1: Full support (UI, text translation, contracts, voice)
 * Tier 2: Partial support (text translation, basic understanding)
 * 
 * Chinese Strategy (MVP):
 * - Official Tier 1 variant: "Chinese (Simplified, Mandarin)" (zh-CN)
 * - UI strings use Simplified characters
 * - Voice/TTS assumes Mandarin pronunciation
 * - Traditional Chinese (zh-TW) reserved for future
 */

export const LANGUAGE_TIERS = {
  TIER_1: 'tier_1',
  TIER_2: 'tier_2'
};

export const LANGUAGES = {
  // Tier 1 - Full Support
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    displayLabel: 'English',
    tier: LANGUAGE_TIERS.TIER_1,
    features: {
      ui: true,
      textTranslation: true,
      contracts: true,
      voice: true
    },
    elevenLabsVoiceId: 'EXAVITQu4vr4xnSDxMaL', // Soft female voice (Sarah)
    samplePhrase: 'Welcome to PocketFiler. We will translate all your deals into your language.',
    notes: 'Default language for the platform.'
  },
  es: {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    displayLabel: 'Español (Spanish)',
    tier: LANGUAGE_TIERS.TIER_1,
    features: {
      ui: true,
      textTranslation: true,
      contracts: true,
      voice: true
    },
    elevenLabsVoiceId: 'EXAVITQu4vr4xnSDxMaL',
    samplePhrase: 'Bienvenido a PocketFiler. Traduciremos todos tus negocios a tu idioma.',
    notes: 'Spanish as spoken in Spain and Latin America.'
  },
  'zh-CN': {
    code: 'zh-CN',
    name: 'Chinese (Simplified, Mandarin)',
    nativeName: '中文',
    displayLabel: '中文 (Chinese, Simplified, Mandarin)',
    tier: LANGUAGE_TIERS.TIER_1,
    features: {
      ui: true,
      textTranslation: true,
      contracts: true,
      voice: true
    },
    elevenLabsVoiceId: 'EXAVITQu4vr4xnSDxMaL',
    samplePhrase: '欢迎使用 PocketFiler。我们将把您的所有交易翻译成您的语言。',
    notes: 'Mandarin pronunciation, Simplified characters. Primary variant for mainland China and Singapore.'
  },
  fr: {
    code: 'fr',
    name: 'French',
    nativeName: 'Français',
    displayLabel: 'Français (French)',
    tier: LANGUAGE_TIERS.TIER_1,
    features: {
      ui: true,
      textTranslation: true,
      contracts: true,
      voice: true
    },
    elevenLabsVoiceId: 'EXAVITQu4vr4xnSDxMaL',
    samplePhrase: 'Bienvenue sur PocketFiler. Nous traduirons toutes vos transactions dans votre langue.',
    notes: 'French as spoken in France and French-speaking regions.'
  },
  de: {
    code: 'de',
    name: 'German',
    nativeName: 'Deutsch',
    displayLabel: 'Deutsch (German)',
    tier: LANGUAGE_TIERS.TIER_1,
    features: {
      ui: true,
      textTranslation: true,
      contracts: true,
      voice: true
    },
    elevenLabsVoiceId: 'EXAVITQu4vr4xnSDxMaL',
    samplePhrase: 'Willkommen bei PocketFiler. Wir übersetzen alle Ihre Geschäfte in Ihre Sprache.',
    notes: 'German as spoken in Germany, Austria, and Switzerland.'
  },
  pt: {
    code: 'pt',
    name: 'Portuguese',
    nativeName: 'Português',
    displayLabel: 'Português (Portuguese)',
    tier: LANGUAGE_TIERS.TIER_1,
    features: {
      ui: true,
      textTranslation: true,
      contracts: true,
      voice: true
    },
    elevenLabsVoiceId: 'EXAVITQu4vr4xnSDxMaL',
    samplePhrase: 'Bem-vindo ao PocketFiler. Traduziremos todos os seus negócios para o seu idioma.',
    notes: 'Portuguese as spoken in Brazil and Portugal.'
  },
  ar: {
    code: 'ar',
    name: 'Arabic',
    nativeName: 'العربية',
    displayLabel: 'العربية (Arabic)',
    tier: LANGUAGE_TIERS.TIER_1,
    features: {
      ui: true,
      textTranslation: true,
      contracts: true,
      voice: true
    },
    elevenLabsVoiceId: 'EXAVITQu4vr4xnSDxMaL',
    samplePhrase: 'مرحبًا بك في PocketFiler. سنترجم جميع صفقاتك إلى لغتك.',
    rtl: true,
    notes: 'Modern Standard Arabic. RTL text direction.'
  },
  hi: {
    code: 'hi',
    name: 'Hindi',
    nativeName: 'हिन्दी',
    displayLabel: 'हिन्दी (Hindi)',
    tier: LANGUAGE_TIERS.TIER_1,
    features: {
      ui: true,
      textTranslation: true,
      contracts: true,
      voice: true
    },
    elevenLabsVoiceId: 'EXAVITQu4vr4xnSDxMaL',
    samplePhrase: 'PocketFiler में आपका स्वागत है। हम आपके सभी सौदों का आपकी भाषा में अनुवाद करेंगे।',
    notes: 'Hindi as spoken in India.'
  },
  ja: {
    code: 'ja',
    name: 'Japanese',
    nativeName: '日本語',
    displayLabel: '日本語 (Japanese)',
    tier: LANGUAGE_TIERS.TIER_1,
    features: {
      ui: true,
      textTranslation: true,
      contracts: true,
      voice: true
    },
    elevenLabsVoiceId: 'EXAVITQu4vr4xnSDxMaL',
    samplePhrase: 'PocketFilerへようこそ。すべての取引をあなたの言語に翻訳します。',
    notes: 'Japanese with standard pronunciation.'
  },
  ko: {
    code: 'ko',
    name: 'Korean',
    nativeName: '한국어',
    displayLabel: '한국어 (Korean)',
    tier: LANGUAGE_TIERS.TIER_1,
    features: {
      ui: true,
      textTranslation: true,
      contracts: true,
      voice: true
    },
    elevenLabsVoiceId: 'EXAVITQu4vr4xnSDxMaL',
    samplePhrase: 'PocketFiler에 오신 것을 환영합니다. 모든 거래를 귀하의 언어로 번역해 드립니다.',
    notes: 'Korean as spoken in South Korea.'
  },
  ru: {
    code: 'ru',
    name: 'Russian',
    nativeName: 'Русский',
    displayLabel: 'Русский (Russian)',
    tier: LANGUAGE_TIERS.TIER_1,
    features: {
      ui: true,
      textTranslation: true,
      contracts: true,
      voice: true
    },
    elevenLabsVoiceId: 'EXAVITQu4vr4xnSDxMaL',
    samplePhrase: 'Добро пожаловать в PocketFiler. Мы переведем все ваши сделки на ваш язык.',
    notes: 'Russian as spoken in Russia.'
  },

  // Tier 2 - Partial Support (text translation only)
  it: {
    code: 'it',
    name: 'Italian',
    nativeName: 'Italiano',
    displayLabel: 'Italiano (Italian)',
    tier: LANGUAGE_TIERS.TIER_2,
    features: {
      ui: false,
      textTranslation: true,
      contracts: false,
      voice: false
    },
    notes: 'Text translation only. UI and contracts fall back to English.'
  },
  nl: {
    code: 'nl',
    name: 'Dutch',
    nativeName: 'Nederlands',
    displayLabel: 'Nederlands (Dutch)',
    tier: LANGUAGE_TIERS.TIER_2,
    features: {
      ui: false,
      textTranslation: true,
      contracts: false,
      voice: false
    }
  },
  pl: {
    code: 'pl',
    name: 'Polish',
    nativeName: 'Polski',
    displayLabel: 'Polski (Polish)',
    tier: LANGUAGE_TIERS.TIER_2,
    features: {
      ui: false,
      textTranslation: true,
      contracts: false,
      voice: false
    }
  },
  tr: {
    code: 'tr',
    name: 'Turkish',
    nativeName: 'Türkçe',
    displayLabel: 'Türkçe (Turkish)',
    tier: LANGUAGE_TIERS.TIER_2,
    features: {
      ui: false,
      textTranslation: true,
      contracts: false,
      voice: false
    }
  },
  vi: {
    code: 'vi',
    name: 'Vietnamese',
    nativeName: 'Tiếng Việt',
    displayLabel: 'Tiếng Việt (Vietnamese)',
    tier: LANGUAGE_TIERS.TIER_1,
    features: {
      ui: true,
      textTranslation: true,
      contracts: true,
      voice: false
    },
    samplePhrase: 'Chào mừng bạn đến với PocketFiler. Chúng tôi sẽ dịch tất cả giao dịch sang ngôn ngữ của bạn.'
  },
  th: {
    code: 'th',
    name: 'Thai',
    nativeName: 'ไทย',
    displayLabel: 'ไทย (Thai)',
    tier: LANGUAGE_TIERS.TIER_2,
    features: {
      ui: false,
      textTranslation: true,
      contracts: false,
      voice: false
    }
  },
  id: {
    code: 'id',
    name: 'Indonesian',
    nativeName: 'Bahasa Indonesia',
    displayLabel: 'Bahasa Indonesia (Indonesian)',
    tier: LANGUAGE_TIERS.TIER_2,
    features: {
      ui: false,
      textTranslation: true,
      contracts: false,
      voice: false
    }
  },
  ms: {
    code: 'ms',
    name: 'Malay',
    nativeName: 'Bahasa Melayu',
    displayLabel: 'Bahasa Melayu (Malay)',
    tier: LANGUAGE_TIERS.TIER_2,
    features: {
      ui: false,
      textTranslation: true,
      contracts: false,
      voice: false
    }
  },
  uk: {
    code: 'uk',
    name: 'Ukrainian',
    nativeName: 'Українська',
    displayLabel: 'Українська (Ukrainian)',
    tier: LANGUAGE_TIERS.TIER_2,
    features: {
      ui: false,
      textTranslation: true,
      contracts: false,
      voice: false
    }
  },
  he: {
    code: 'he',
    name: 'Hebrew',
    nativeName: 'עברית',
    displayLabel: 'עברית (Hebrew)',
    tier: LANGUAGE_TIERS.TIER_2,
    features: {
      ui: false,
      textTranslation: true,
      contracts: false,
      voice: false
    },
    rtl: true
  },

  // Future/Planned - Not yet enabled
  'zh-TW': {
    code: 'zh-TW',
    name: 'Chinese (Traditional)',
    nativeName: '繁體中文',
    displayLabel: '繁體中文 (Chinese, Traditional)',
    tier: 'planned',
    features: {
      ui: false,
      textTranslation: true,
      contracts: false,
      voice: true
    },
    notes: 'Traditional script used in Taiwan, Hong Kong. Future roadmap.',
    planned: true
  }
};

// Backward compatibility alias for old 'zh' code
LANGUAGES.zh = LANGUAGES['zh-CN'];

// Helper functions - filter out alias codes to prevent duplicates
export const getTier1Languages = () => 
  Object.entries(LANGUAGES)
    .filter(([code, lang]) => lang.tier === LANGUAGE_TIERS.TIER_1 && code !== 'zh')
    .map(([, lang]) => lang);

export const getTier2Languages = () => 
  Object.entries(LANGUAGES)
    .filter(([code, lang]) => lang.tier === LANGUAGE_TIERS.TIER_2)
    .map(([, lang]) => lang);

export const getAllLanguages = () => 
  Object.entries(LANGUAGES)
    .filter(([code, lang]) => lang.tier !== 'planned' && code !== 'zh')
    .map(([, lang]) => lang);

export const getLanguageByCode = (code) => {
  // Handle legacy 'zh' code
  if (code === 'zh') return LANGUAGES['zh-CN'];
  return LANGUAGES[code] || null;
};

export const getContractLanguages = () => 
  Object.entries(LANGUAGES)
    .filter(([code, lang]) => lang.features?.contracts && code !== 'zh')
    .map(([, lang]) => lang);

export const getVoiceLanguages = () => 
  Object.entries(LANGUAGES)
    .filter(([code, lang]) => lang.features?.voice && code !== 'zh')
    .map(([, lang]) => lang);

export const isContractLanguageSupported = (code) => {
  const lang = getLanguageByCode(code);
  return lang?.features?.contracts || false;
};

export const isVoiceSupported = (code) => {
  const lang = getLanguageByCode(code);
  return lang?.features?.voice || false;
};

// Default ElevenLabs voice configuration - soft, friendly female voice
export const ELEVENLABS_CONFIG = {
  defaultVoiceId: 'EXAVITQu4vr4xnSDxMaL', // Sarah voice
  defaultVoiceName: 'Sarah',
  voiceSettings: {
    stability: 0.5,
    similarity_boost: 0.75,
    style: 0.0,
    use_speaker_boost: true
  }
};

// Local storage key for language preference
export const LANGUAGE_STORAGE_KEY = 'pocketfiler_language';

// Get stored language preference
export const getStoredLanguage = () => {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    return null;
  }
};

// Set language preference in localStorage
export const setStoredLanguage = (code) => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch {
    // localStorage not available
  }
};

// UX Copy patterns for clarification
export const CLARIFICATION_COPY = {
  // For chat recipient when no direct translation exists
  chatRecipientNotice: (explanation) => 
    `This part does not have a direct translation into your language. It most likely means: "${explanation}". Would you like me to ask the other party to confirm this meaning?`,
  
  // Draft message for clarification request
  clarificationRequest: (originalPhrase, proposedMeaning) =>
    `To avoid misunderstandings, we want to confirm that when you say "${originalPhrase}", you mean: "${proposedMeaning}". Please confirm or correct this.`,
  
  // Contract notice for ambiguous clauses
  contractNotice: 
    'AI document analysis: One or more clauses do not have a direct equivalent between your selected languages. We have used descriptive language instead. Please review these sections carefully and confirm with the other party if needed.',
  
  // Call summary note (for future use)
  callSummaryNote: 
    'Some phrases were approximated due to language differences. Review the highlighted sections in the transcript and consider sending a clarification message before signing any contracts.',
  
  // Tier 2 contract fallback notice
  tier2ContractNotice: (tier2Lang, tier1Lang) =>
    `"${tier2Lang}" has limited contract support. The authoritative contract text is in English and ${tier1Lang}. A reference translation in ${tier2Lang} is provided for convenience only and should not be relied upon for legal purposes.`
};

// Language picker UI text (translated)
export const LANGUAGE_PICKER_TEXT = {
  en: {
    title: 'Choose your language',
    subtitle: "We'll show all instructions, messages, and contracts in this language. Other people can choose their own language; AI will handle the translation for you.",
    continue: 'Continue',
    playSample: 'Play sample',
    playing: 'Playing...'
  },
  es: {
    title: 'Elige tu idioma',
    subtitle: 'Te mostraremos todas las instrucciones, mensajes y contratos en este idioma. Otras personas pueden elegir su propio idioma; la IA se encargará de la traducción por ti.',
    continue: 'Continuar',
    playSample: 'Reproducir muestra',
    playing: 'Reproduciendo...'
  },
  'zh-CN': {
    title: '选择您的语言',
    subtitle: '我们将以此语言向您显示所有说明、消息和合同。其他人可以选择自己的语言；AI将为您处理翻译。',
    continue: '继续',
    playSample: '播放示例',
    playing: '播放中...'
  },
  fr: {
    title: 'Choisissez votre langue',
    subtitle: 'Nous vous montrerons toutes les instructions, messages et contrats dans cette langue. Les autres peuvent choisir leur propre langue ; l\'IA s\'occupera de la traduction pour vous.',
    continue: 'Continuer',
    playSample: 'Écouter un exemple',
    playing: 'Lecture...'
  },
  de: {
    title: 'Wählen Sie Ihre Sprache',
    subtitle: 'Wir zeigen Ihnen alle Anweisungen, Nachrichten und Verträge in dieser Sprache. Andere können ihre eigene Sprache wählen; die KI übernimmt die Übersetzung für Sie.',
    continue: 'Weiter',
    playSample: 'Beispiel abspielen',
    playing: 'Wird abgespielt...'
  },
  pt: {
    title: 'Escolha seu idioma',
    subtitle: 'Mostraremos todas as instruções, mensagens e contratos neste idioma. Outras pessoas podem escolher seu próprio idioma; a IA cuidará da tradução para você.',
    continue: 'Continuar',
    playSample: 'Reproduzir amostra',
    playing: 'Reproduzindo...'
  },
  ar: {
    title: 'اختر لغتك',
    subtitle: 'سنعرض لك جميع التعليمات والرسائل والعقود بهذه اللغة. يمكن للآخرين اختيار لغتهم الخاصة؛ سيتولى الذكاء الاصطناعي الترجمة نيابة عنك.',
    continue: 'متابعة',
    playSample: 'تشغيل عينة',
    playing: 'جارٍ التشغيل...'
  },
  hi: {
    title: 'अपनी भाषा चुनें',
    subtitle: 'हम आपको इस भाषा में सभी निर्देश, संदेश और अनुबंध दिखाएंगे। अन्य लोग अपनी भाषा चुन सकते हैं; AI आपके लिए अनुवाद संभालेगा।',
    continue: 'जारी रखें',
    playSample: 'नमूना चलाएं',
    playing: 'चल रहा है...'
  },
  ja: {
    title: '言語を選択してください',
    subtitle: 'すべての説明、メッセージ、契約をこの言語で表示します。他の人は自分の言語を選択できます。AIが翻訳を処理します。',
    continue: '続ける',
    playSample: 'サンプルを再生',
    playing: '再生中...'
  },
  ko: {
    title: '언어를 선택하세요',
    subtitle: '모든 안내, 메시지, 계약서를 이 언어로 보여드립니다. 다른 사람들은 자신의 언어를 선택할 수 있으며, AI가 번역을 처리합니다.',
    continue: '계속',
    playSample: '샘플 재생',
    playing: '재생 중...'
  },
  ru: {
    title: 'Выберите ваш язык',
    subtitle: 'Мы покажем все инструкции, сообщения и контракты на этом языке. Другие могут выбрать свой язык; ИИ позаботится о переводе.',
    continue: 'Продолжить',
    playSample: 'Прослушать пример',
    playing: 'Воспроизведение...'
  }
};

export default LANGUAGES;
